import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { Info, MagnifyingGlass, ArrowDown, FunnelSimple, Trophy, X, Fire, ClockCounterClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import { teleproApi } from "../lib/teleproApi";
import { STATUSES, RESOLVED_STATUSES } from "./telepro/callListConstants";
import { playDingSound } from "./telepro/callListHelpers";
import BusinessRow from "./telepro/BusinessRow";
import ConfettiBurst from "./telepro/ConfettiBurst";
import SimpleCallView from "./telepro/SimpleCallView";
import LiveStatsBanner from "./telepro/LiveStatsBanner";
import SessionHUD from "./telepro/SessionHUD";
import { useProspectingSession } from "./telepro/useProspectingSession";
import { useKeyboardShortcuts } from "./telepro/useKeyboardShortcuts";

/** Detect mobile once at mount to pick a sensible default mode. */
function detectMobileDefault() {
  if (typeof window === "undefined") return "simple";
  try {
    const saved = localStorage.getItem("mc68_telepro_call_mode");
    if (saved === "simple" || saved === "expert") return saved;
  } catch { /* ignore */ }
  return window.matchMedia("(max-width: 768px)").matches ? "simple" : "expert";
}

/**
 * Interactive call-tracker for ONE daily list.
 *
 * Power-features:
 * - Filter toggle "À traiter seulement" → hides resolved rows.
 * - Floating "Suivant →" button → smooth-scrolls to next to_call row.
 * - One-tap quick actions per row (Pas joint / Refus / Rappeler).
 * - Progress bar with daily-goal milestone.
 * - Confetti + tiny ding when a row becomes "sale".
 */
export default function TeleproCallList({ listId, dailyGoal = 60, telepro = null, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [editingNote, setEditingNote] = useState(null);
  const [showOnlyTodo, setShowOnlyTodo] = useState(false);
  const [confetti, setConfetti] = useState(false);
  const [assistOpenId, setAssistOpenId] = useState(null);
  const [mode, setMode] = useState(detectMobileDefault);
  const [session, setSession] = useState(null);
  const [untreated, setUntreated] = useState(null);
  const [repriseBannerOpen, setRepriseBannerOpen] = useState(true);
  // Phase 3 — persist the "hide low-quality" toggle per télépro
  const [hideLowQuality, setHideLowQuality] = useState(() => {
    try { return localStorage.getItem("telepro:hideLowQuality") === "1"; }
    catch { return false; }
  });
  const toggleHideLowQuality = () => {
    setHideLowQuality((v) => {
      const n = !v;
      try { localStorage.setItem("telepro:hideLowQuality", n ? "1" : "0"); } catch {}
      return n;
    });
  };
  const listRef = useRef(null);
  // Vague 13 — Session timer (début de session locale, pour appels/h) & minuteur d'appel courant
  const [sessionStartAt] = useState(() => Date.now());
  const [callStartAt, setCallStartAt] = useState(null);
  const startCall = useCallback(() => setCallStartAt(Date.now()), []);
  const stopCall = useCallback(() => setCallStartAt(null), []);

  // Phase 2 — prospecting session lifecycle (start/heartbeat/end)
  useProspectingSession(listId, (payload) => {
    setSession(payload.session || null);
    setUntreated(typeof payload.untreated === "number" ? payload.untreated : null);
  });

  const setModeAndPersist = useCallback((m) => {
    setMode(m);
    try { localStorage.setItem("mc68_telepro_call_mode", m); } catch { /* ignore */ }
  }, []);

  const lastMutation = useRef(0);
  const load = useCallback(() => {
    setLoading(true);
    const started = Date.now();
    teleproApi.get(`/me/lists/${listId}/businesses`)
      // ignore a response that started before a local change (would revert the optimistic update)
      .then((r) => { if (lastMutation.current <= started) setData(r.data); })
      .catch((e) => setError(e?.response?.data?.detail || "Chargement impossible"))
      .finally(() => setLoading(false));
  }, [listId]);

  useEffect(() => { load(); }, [load]);

  const updateCall = async (businessId, patch, opts = {}) => {
    const current = data.businesses.find((b) => b.id === businessId);
    if (!current) return;
    lastMutation.current = Date.now();
    const prevStatus = current.call.status;
    const prevNote = current.call.note;
    // Optimistic overlay: flat patch keys map 1:1, but the 3 prospect-answer
    // keys nest into `prospect_answers` so the AssistPanel radios/checkboxes
    // re-render selected immediately.
    const nestedAnswers = { ...(current.call.prospect_answers || {}) };
    if ("prospect_found_via" in patch) nestedAnswers.found_via = patch.prospect_found_via;
    if ("prospect_has_website" in patch) nestedAnswers.has_website = patch.prospect_has_website;
    if ("prospect_interest" in patch) nestedAnswers.interest = patch.prospect_interest;
    const flatPatch = Object.fromEntries(
      Object.entries(patch).filter(([k]) => !k.startsWith("prospect_"))
    );
    const next = { ...current.call, ...flatPatch, prospect_answers: nestedAnswers };
    setData((d) => {
      const nextList = d.businesses.map((b) => b.id === businessId ? { ...b, call: next } : b);
      const counts = {};
      STATUSES.forEach((s) => { counts[s.id] = 0; });
      nextList.forEach((b) => { counts[b.call.status] = (counts[b.call.status] || 0) + 1; });
      return { ...d, businesses: nextList, counts };
    });
    if (next.status === "sale" && prevStatus !== "sale") {
      setConfetti(true);
      playDingSound();
      setTimeout(() => setConfetti(false), 2400);
    }
    try {
      // Only send the fields explicitly present in `patch` so we don't blank out
      // existing server values with stale optimistic state.
      const body = {};
      ["status", "note", "callback_at", "contact_method",
       "contact_phone", "contact_email", "contact_name", "contact_role", "callback_reason",
       "prospect_found_via", "prospect_has_website", "prospect_interest",
       "refusal_reason", "hot_lead_offer", "website_lead"].forEach((k) => {
        if (Object.prototype.hasOwnProperty.call(patch, k)) body[k] = patch[k];
      });
      const resp = await teleproApi.patch(`/me/lists/${listId}/calls/${businessId}`, body);
      const unreach = resp?.data?.unreachable?.state;
      if (unreach === "unreachable_temp") toast.info("3 tentatives sans réponse : prospect mis de côté 60 jours");
      else if (unreach === "unreachable_final") toast.info("Prospect classé « injoignable » : il ne sera plus redistribué");
      // Opposition RGPD : définitive, pas d'annulation possible côté télépro
      if (patch.refusal_reason === "no_more_contact") {
        toast.success("Opposition enregistrée : ce prospect ne sera plus contacté");
      } else
      // Vague 13 — Undo toast (uniquement pour les changements de statut)
      if ("status" in patch && patch.status !== prevStatus && !opts.silent) {
        const label = STATUSES.find((s) => s.id === patch.status)?.short || patch.status;
        toast.success(`✓ ${label}`, {
          duration: 5000,
          action: {
            label: "Annuler",
            onClick: async () => {
              try {
                await teleproApi.patch(`/me/lists/${listId}/calls/${businessId}`, {
                  status: prevStatus,
                  note: prevNote,
                });
                lastMutation.current = 0;
                load();
                toast.info("Action annulée");
              } catch {
                toast.error("Impossible d'annuler");
              }
            },
          },
        });
      }
      if (opts.jumpNext) jumpToNextToCall(businessId);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sauvegarde impossible");
      lastMutation.current = 0;
      load();
    }
  };

  const jumpToNextToCall = (afterId) => {
    if (!data) return;
    const arr = data.businesses;
    const idx = afterId ? arr.findIndex((b) => b.id === afterId) : -1;
    const next = arr.slice(idx + 1).find((b) => b.call.status === "to_call")
              || arr.find((b) => b.call.status === "to_call");
    if (!next) {
      toast.success("Tu as traité toutes les entreprises de ta liste !");
      return;
    }
    const el = document.querySelector(`[data-testid="call-row-${next.id}"]`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("ring-2", "ring-primary", "ring-offset-2");
      setTimeout(() => el.classList.remove("ring-2", "ring-primary", "ring-offset-2"), 1500);
    }
  };

  // Vague 6 — raccourcis clavier globaux (uniquement en mode "expert" liste)
  const [shortcutsHelpOpen, setShortcutsHelpOpen] = useState(false);
  const getCurrentRow = useCallback(() => {
    if (!data) return null;
    return data.businesses.find((b) => b.call.status === "to_call") || null;
  }, [data]);
  useKeyboardShortcuts({
    enabled: mode === "expert",
    onAction: (action) => {
      if (action.type === "help") { setShortcutsHelpOpen((v) => !v); return; }
      if (action.type === "next") { jumpToNextToCall(null); return; }
      const cur = getCurrentRow();
      if (!cur) { toast.info("Aucune fiche à appeler — liste terminée 🎯"); return; }
      if (action.type === "set-status") {
        updateCall(cur.id, { status: action.status }, { jumpNext: true });
        toast.success(`« ${cur.name?.slice(0, 32)} » → ${action.status}`);
      } else if (action.type === "toggle-assist") {
        setAssistOpenId((v) => v === cur.id ? null : cur.id);
      }
    },
  });

  const visibleBusinesses = useMemo(() => {
    if (!data) return [];
    let arr = data.businesses;
    if (showOnlyTodo) arr = arr.filter((b) => !RESOLVED_STATUSES.has(b.call.status));
    if (hideLowQuality) arr = arr.filter((b) => !(b.quality_tier === "Faible" || b.quality_tier === "Exclure"));
    return arr;
  }, [data, showOnlyTodo, hideLowQuality]);
  const hiddenLowQualityCount = useMemo(() => {
    if (!data || !hideLowQuality) return 0;
    return data.businesses.filter((b) => b.quality_tier === "Faible" || b.quality_tier === "Exclure").length;
  }, [data, hideLowQuality]);

  if (loading) return <div className="text-sm text-muted-foreground py-6 text-center">Chargement de ta liste…</div>;
  if (error) return <div className="text-sm text-secondary bg-secondary/10 border border-secondary/30 rounded-sm p-3">{error}</div>;
  if (!data) return null;

  const total = data.businesses.length;
  const handled = STATUSES.filter((s) => s.id !== "to_call").reduce((sum, s) => sum + (data.counts[s.id] || 0), 0);
  const goal = Math.min(total, dailyGoal || 60);
  const goalPct = Math.min(100, Math.round((handled / goal) * 100));
  const remainingToCall = data.counts.to_call || 0;
  const salesCount = data.counts.sale || 0;

  return (
    <div className="space-y-4 relative" ref={listRef} data-testid="call-list-view">
      {confetti && <ConfettiBurst/>}

      {/* Vague 13 — HUD sticky : progression + rappels + ventes + timer + cadence */}
      <SessionHUD
        handled={handled}
        total={total}
        goal={goal}
        sales={salesCount}
        callbacksDueToday={data.callbacks_due_today || 0}
        callStartAt={callStartAt}
        onStopCall={stopCall}
        sessionStartAt={sessionStartAt}
      />

      {/* Bandeau rouge « 🔴 Chiffres live à citer » — visible tout le temps
          en tête du portail (mode simple ET mode liste). Le télépro l'a
          sous les yeux avant chaque appel. */}
      <LiveStatsBanner />

      {/* Sélecteur mode simple / expert — priorité mobile */}
      <div className="flex items-center gap-1 bg-muted/40 rounded-2xl p-1 sticky top-2 z-20 self-start" data-testid="call-mode-switcher">
        <button
          type="button"
          onClick={() => setModeAndPersist("simple")}
          className={`px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1.5 transition-colors ${mode === "simple" ? "bg-primary text-primary-foreground shadow" : "text-muted-foreground hover:text-foreground"}`}
          data-testid="mode-btn-simple"
          aria-pressed={mode === "simple"}
        >
          🎯 Simple
        </button>
        <button
          type="button"
          onClick={() => setModeAndPersist("expert")}
          className={`px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1.5 transition-colors ${mode === "expert" ? "bg-primary text-primary-foreground shadow" : "text-muted-foreground hover:text-foreground"}`}
          data-testid="mode-btn-expert"
          aria-pressed={mode === "expert"}
        >
          📋 Liste
        </button>
      </div>

      {mode === "simple" ? (
        <SimpleCallView
          businesses={data.businesses}
          telepro={telepro}
          callStartAt={callStartAt}
          onStartCall={startCall}
          onStopCall={stopCall}
          onUpdate={(businessId, patch) => updateCall(businessId, patch, {})}
          onSwitchExpert={() => setModeAndPersist("expert")}
        />
      ) : (
        <>

      {session?.resumed && untreated > 0 && repriseBannerOpen && (
        <div
          className="bg-primary/10 border-l-4 border-primary rounded-sm p-3 flex items-start gap-2.5"
          data-testid="reprise-banner"
        >
          <ClockCounterClockwise size={18} weight="fill" className="text-primary shrink-0 mt-0.5"/>
          <div className="text-xs leading-relaxed text-foreground/90 flex-1">
            <b className="text-primary block mb-0.5">
              👋 Bon retour ! Tu as <span className="tabular-nums">{untreated}</span> {untreated > 1 ? "prospects non traités" : "prospect non traité"} sur ta liste du jour.
            </b>
            Ta session précédente a été reprise — continue là où tu t’étais arrêté(e). Utilise le bouton « Suivant → » en bas à droite pour sauter directement au prochain à appeler.
          </div>
          <button
            type="button"
            onClick={() => setRepriseBannerOpen(false)}
            className="text-muted-foreground hover:text-foreground p-1"
            aria-label="Fermer"
            data-testid="reprise-banner-close"
          >
            <X size={14}/>
          </button>
        </div>
      )}

      {session && !session.resumed && untreated > 0 && repriseBannerOpen && (
        <div
          className="bg-muted/50 border border-border rounded-sm px-3 py-2 flex items-center gap-2 text-xs"
          data-testid="session-fresh-banner"
        >
          <ClockCounterClockwise size={14} weight="duotone" className="text-muted-foreground"/>
          <span className="text-muted-foreground">
            Nouvelle session démarrée · <b className="text-foreground tabular-nums">{untreated}</b> {untreated > 1 ? "prospects à traiter" : "prospect à traiter"}
          </span>
          <button
            type="button"
            onClick={() => setRepriseBannerOpen(false)}
            className="ml-auto text-muted-foreground hover:text-foreground"
            aria-label="Fermer"
            data-testid="session-fresh-close"
          >
            <X size={12}/>
          </button>
        </div>
      )}

      {data.callbacks_due_today > 0 && (
        <div
          className="bg-secondary/10 border-l-4 border-secondary rounded-sm p-3 flex items-start gap-2.5"
          data-testid="callbacks-due-banner"
        >
          <span className="text-secondary text-xl leading-none shrink-0">🔔</span>
          <div className="text-xs leading-relaxed text-foreground/90 flex-1">
            <b className="text-secondary block mb-0.5">
              {data.callbacks_due_today} {data.callbacks_due_today > 1 ? "rappels programmés" : "rappel programmé"} pour aujourd'hui !
            </b>
            {data.callbacks_due_today > 1 ? "Ces fiches sont" : "Cette fiche est"} placée{data.callbacks_due_today > 1 ? "s" : ""} EN TÊTE avec un badge <span className="inline-flex items-center gap-0.5 align-middle bg-secondary/15 text-secondary border border-secondary/40 rounded-sm px-1.5 py-0.5 text-[10px] font-bold uppercase">🔔 À rappeler</span>. Traite-les en premier — c'est un signal chaud qui refroidit vite.
          </div>
        </div>
      )}

      {data.dup_alert_count > 0 && (
        <div
          className="bg-orange-500/10 border-l-4 border-orange-500 rounded-sm p-3 flex items-start gap-2.5"
          data-testid="dup-alert-banner"
        >
          <span className="text-orange-600 text-xl leading-none shrink-0">⚠️</span>
          <div className="text-xs leading-relaxed text-foreground/90 flex-1">
            <b className="text-orange-800 block mb-0.5">
              {data.dup_alert_count} {data.dup_alert_count > 1 ? "fiches" : "fiche"} déjà appelée{data.dup_alert_count > 1 ? "s" : ""} par un(e) collègue cette semaine
            </b>
            Regarde le petit encart orange <span className="inline-flex items-center gap-0.5 align-middle bg-orange-500/15 text-orange-800 border border-orange-500/40 rounded-sm px-1.5 py-0.5 text-[10px] font-bold uppercase">⚠ Déjà appelée</span> sur chaque fiche concernée. Adapte ton intro (« un collègue vous a peut-être contacté… ») ou passe à la suivante si un refus est déjà noté.
          </div>
        </div>
      )}

      {data.priority_count > 0 && (
        <div
          className="bg-secondary/10 border-l-4 border-secondary rounded-sm p-3 flex items-start gap-2.5"
          data-testid="priority-banner"
        >
          <Fire size={18} weight="fill" className="text-secondary shrink-0 mt-0.5"/>
          <div className="text-xs leading-relaxed text-foreground/90 flex-1">
            <b className="text-secondary block mb-0.5">
              🔥 {data.priority_count} {data.priority_count > 1 ? "entreprises prioritaires" : "entreprise prioritaire"} — appelle-les EN PREMIER !
            </b>
            Ces fiches sont ressorties dans les recherches récentes de visiteurs (ou couvrent des recherches vides). Elles sont classées en tête de ta liste avec un badge <span className="inline-flex items-center gap-0.5 align-middle bg-secondary/15 text-secondary border border-secondary/40 rounded-sm px-1.5 py-0.5 text-[10px] font-bold uppercase"><Fire size={9} weight="fill"/> Priorité</span>. Focus dessus : elles ont le meilleur taux de closing.
          </div>
        </div>
      )}

      <div className="bg-[#E0B043]/10 border border-[#E0B043]/40 rounded-sm p-3 flex items-start gap-2.5" data-testid="contacts-info-banner">
        <Info size={16} weight="duotone" className="text-[#8a6a14] shrink-0 mt-0.5"/>
        <div className="text-xs leading-relaxed text-foreground/90">
          <b className="text-foreground">Numéros de téléphone ou emails manquants ?</b> C&apos;est normal — notre annuaire SIREN ne contient pas toujours ces infos.
          Pour chaque entreprise, clique sur le bouton <span className="inline-flex items-center gap-0.5 align-middle bg-card border border-border rounded-sm px-1.5 py-0.5 font-semibold"><MagnifyingGlass size={9} weight="bold"/> Google</span> pour ouvrir une recherche pré-remplie
          (nom + ville + « contact ») dans un nouvel onglet.
        </div>
      </div>

      <div className="bg-card border border-border rounded-sm p-3 sm:p-4" data-testid="progress-block">
        <div className="flex items-center justify-between gap-3 mb-2">
          <div>
            <div className="label-uppercase mb-0.5">Progression du jour</div>
            <div className="font-display text-lg font-medium">
              <span className="tabular-nums text-foreground">{handled}</span>
              <span className="text-muted-foreground"> / {goal} appels traités</span>
              {handled >= goal && <span className="ml-2 text-primary text-sm">🎯 Objectif atteint !</span>}
            </div>
          </div>
          {onClose && (
            <button onClick={onClose} className="text-muted-foreground hover:text-foreground p-1" aria-label="Fermer" data-testid="call-list-close">
              <X size={16}/>
            </button>
          )}
        </div>
        <div className="h-2 bg-muted rounded-full overflow-hidden" data-testid="progress-bar">
          <div
            className={`h-full transition-all duration-700 ${handled >= goal ? "bg-primary" : "bg-gradient-to-r from-primary/70 to-primary"}`}
            style={{ width: `${goalPct}%` }}
          />
        </div>
        <div className="flex items-center justify-between mt-1 text-[11px] text-muted-foreground">
          <span>{goalPct}% de l&apos;objectif</span>
          <span>Reste {remainingToCall} {remainingToCall > 1 ? "entreprises" : "entreprise"} à appeler</span>
        </div>
      </div>

      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-7 gap-1.5 flex-1 min-w-[280px]">
          {STATUSES.map((s) => (
            <div key={s.id} className={`border rounded-sm px-2 py-1.5 text-center ${s.color}`} data-testid={`stat-${s.id}`}>
              <div className="font-display text-base font-semibold tabular-nums">{data.counts[s.id] || 0}</div>
              <div className="text-[10px] uppercase tracking-wider font-bold">{s.short}</div>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <label className="inline-flex items-center gap-2 text-xs cursor-pointer select-none bg-card border border-border rounded-sm px-3 py-1.5 hover:border-primary transition-colors" data-testid="filter-todo-toggle">
            <input
              type="checkbox"
              checked={showOnlyTodo}
              onChange={(e) => setShowOnlyTodo(e.target.checked)}
              className="accent-primary"
            />
            <FunnelSimple size={12} weight="bold"/>
            <span className="font-semibold">À traiter seulement</span>
          </label>
          <label className="inline-flex items-center gap-2 text-xs cursor-pointer select-none bg-card border border-border rounded-sm px-3 py-1.5 hover:border-primary transition-colors" data-testid="filter-hide-low-quality-toggle">
            <input
              type="checkbox"
              checked={hideLowQuality}
              onChange={toggleHideLowQuality}
              className="accent-primary"
            />
            ⭐ <span className="font-semibold">Masquer les Faible</span>
            {hiddenLowQualityCount > 0 && (
              <span className="text-[10px] font-mono bg-muted/60 text-muted-foreground rounded-sm px-1 py-0.5" data-testid="hidden-low-quality-count">
                −{hiddenLowQualityCount}
              </span>
            )}
          </label>
        </div>
      </div>

      {/* VAGUE 18d — Légende code couleur */}
      <details className="bg-card border border-border rounded-sm p-2.5 text-xs" data-testid="color-legend">
        <summary className="cursor-pointer font-semibold flex items-center gap-2 text-foreground">
          <span className="inline-flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-emerald-500"/>
            <span className="w-2 h-2 rounded-full bg-amber-500"/>
            <span className="w-2 h-2 rounded-full bg-rose-500"/>
          </span>
          Code couleur des prospects
        </summary>
        <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-2 text-[11px] text-muted-foreground">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 bg-emerald-100 text-emerald-800 border border-emerald-300 font-bold uppercase text-[10px]">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"/>Abonné
            </span>
            <span>Client Premium — ne pas rappeler</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 bg-amber-100 text-amber-800 border border-amber-300 font-bold uppercase text-[10px]">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500"/>En cours
            </span>
            <span>Opportunité en cours (intéressé / RDV / rappel)</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 bg-rose-100 text-rose-800 border border-rose-300 font-bold uppercase text-[10px]">
              <span className="w-1.5 h-1.5 rounded-full bg-rose-500"/>Déjà appelé
            </span>
            <span>Déjà contacté récemment — vérifier avant d'appeler</span>
          </div>
        </div>
      </details>

      {visibleBusinesses.length === 0 ? (
        <div className="bg-primary/5 border border-primary/30 rounded-sm p-6 text-center" data-testid="all-done-state">
          <Trophy size={28} weight="duotone" className="text-primary mx-auto mb-2"/>
          <div className="font-display text-lg font-medium">Tu as traité toutes les entreprises restantes !</div>
          <div className="text-xs text-muted-foreground mt-1">
            Décoche « À traiter seulement » pour revoir les fiches déjà appelées.
          </div>
        </div>
      ) : (
        <ul className="bg-card border border-border rounded-sm divide-y divide-border">
          {visibleBusinesses.map((b) => (
            <BusinessRow
              key={b.id}
              biz={b}
              telepro={telepro}
              onStartCall={startCall}
              onUpdate={(patch, opts) => updateCall(b.id, patch, opts)}
              isEditingNote={editingNote === b.id}
              onStartEditNote={() => setEditingNote(b.id)}
              onCancelEditNote={() => setEditingNote(null)}
              assistOpen={assistOpenId === b.id}
              onToggleAssist={() => setAssistOpenId((cur) => cur === b.id ? null : b.id)}
            />
          ))}
        </ul>
      )}

      {mode === "expert" && remainingToCall > 0 && (
        <button
          onClick={() => jumpToNextToCall(null)}
          className="fixed bottom-6 right-6 z-30 bg-primary text-primary-foreground hover:bg-[#0f3d29] hover:text-white shadow-lg rounded-full pl-4 pr-3 py-2.5 text-sm font-semibold inline-flex items-center gap-1.5 transition-transform hover:scale-105"
          data-testid="next-todo-btn"
          title="Aller à la prochaine entreprise à appeler (raccourci : N)"
        >
          Suivant <ArrowDown size={14} weight="bold"/>
          <span className="bg-white/20 rounded-full text-[10px] px-1.5 py-0.5 ml-1 tabular-nums">{remainingToCall}</span>
        </button>
      )}

      {/* Vague 6 — Hint raccourcis clavier (uniquement mode expert desktop) */}
      {mode === "expert" && (
        <button
          type="button"
          onClick={() => setShortcutsHelpOpen(true)}
          className="fixed bottom-6 left-6 z-30 bg-muted/80 hover:bg-muted text-foreground/70 hover:text-foreground shadow rounded-full w-8 h-8 text-xs font-mono border border-border transition-colors hidden sm:inline-flex items-center justify-center"
          data-testid="shortcuts-help-btn"
          title="Voir les raccourcis clavier (?)"
        >
          ?
        </button>
      )}
      {shortcutsHelpOpen && (
        <>
          <div className="fixed inset-0 bg-black/40 z-40" onClick={() => setShortcutsHelpOpen(false)}/>
          <div
            className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 bg-card border border-border rounded-sm shadow-xl w-[92%] max-w-md p-5"
            data-testid="shortcuts-help-modal"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="font-display text-lg font-medium">⌨️ Raccourcis clavier</div>
              <button onClick={() => setShortcutsHelpOpen(false)} className="text-muted-foreground hover:text-foreground p-1" data-testid="shortcuts-help-close">
                <X size={14}/>
              </button>
            </div>
            <div className="text-xs text-muted-foreground mb-3">
              La fiche « courante » = la première encore <b>à appeler</b> dans ta liste. Les raccourcis sont désactivés quand tu tapes dans un champ.
            </div>
            <ul className="text-sm space-y-1.5">
              {[
                ["1", "Pas joint"],
                ["2", "Refus"],
                ["3", "Rappeler"],
                ["4", "Intéressé"],
                ["5", "Messagerie"],
                ["6", "Faux numéro"],
                ["N", "Fiche suivante"],
                ["Espace", "Ouvrir/fermer mode appel"],
                ["?", "Afficher cette aide"],
              ].map(([key, label]) => (
                <li key={key} className="flex items-center justify-between py-1 border-b border-border/50">
                  <span>{label}</span>
                  <kbd className="font-mono bg-muted border border-border rounded-sm px-2 py-0.5 text-xs font-bold text-primary">{key}</kbd>
                </li>
              ))}
            </ul>
          </div>
        </>
      )}
        </>
      )}
    </div>
  );
}
